import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    fetchSchedulerTasks,
    triggerSchedulerTask,
    updateSchedulerTask,
    fetchSchedulerTaskRuns,
} from '../../store/slices/schedulerSlice';

function formatRelativeTime(ts) {
    if (!ts) return '—';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

function formatNextRun(ts) {
    if (!ts) return '—';
    const diff = new Date(ts).getTime() - Date.now();
    if (diff <= 0) return 'soon';
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `in ${hrs}h`;
    return `in ${Math.floor(hrs / 24)}d`;
}

function StatusBadge({ status }) {
    const colors = {
        active: 'bg-green-100 text-green-800',
        revoked: 'bg-red-100 text-red-800',
        needs_reauth: 'bg-yellow-100 text-yellow-800',
        none: 'bg-gray-100 text-gray-600',
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
            {status || 'unknown'}
        </span>
    );
}

function RunStatusIcon({ status }) {
    if (status === 'completed') return <span className="text-green-600" title="Completed">✓</span>;
    if (status === 'failed') return <span className="text-red-600" title="Failed">✗</span>;
    return <span className="text-yellow-600" title="Running">⟳</span>;
}

function PresetSelect({ value, onChange, disabled }) {
    return (
        <select
            value={value || 'disabled'}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="text-sm border border-gray-300 rounded px-2 py-1 disabled:opacity-50"
        >
            <option value="disabled">Disabled</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
        </select>
    );
}

function RunHistoryPanel({ dataSourceId, onClose }) {
    const dispatch = useDispatch();
    const runs = useSelector((state) => state.scheduler.runsByDataSource[dataSourceId] || []);

    useEffect(() => {
        dispatch(fetchSchedulerTaskRuns({ dataSourceId, limit: 20 }));
    }, [dispatch, dataSourceId]);

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-4">
            <div className="fixed inset-0 bg-black/30" onClick={onClose} />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="font-semibold text-gray-900">Run History</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                </div>
                <div className="overflow-y-auto flex-1">
                    {runs.length === 0 ? (
                        <p className="text-gray-500 text-sm p-4">No runs recorded yet.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                                <tr>
                                    <th className="px-4 py-2 text-left">Status</th>
                                    <th className="px-4 py-2 text-left">Triggered</th>
                                    <th className="px-4 py-2 text-left">Started</th>
                                    <th className="px-4 py-2 text-left">Records</th>
                                    <th className="px-4 py-2 text-left">Error</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {runs.map((run) => (
                                    <tr key={run.run_id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2">
                                            <RunStatusIcon status={run.status} /> {run.status}
                                        </td>
                                        <td className="px-4 py-2">{run.triggered_by}</td>
                                        <td className="px-4 py-2">{formatRelativeTime(run.started_at)}</td>
                                        <td className="px-4 py-2">{run.records_synced ?? '—'}</td>
                                        <td className="px-4 py-2 text-red-600 max-w-xs truncate" title={run.error_message || ''}>
                                            {run.error_message || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function PipelinesTab() {
    const dispatch = useDispatch();
    // App-wide isAdmin pattern: admin role OR god-mode (D21, Sidebar.jsx:6)
    const { user, activeTenant } = useSelector((state) => state.auth);
    const isAdmin = activeTenant?.role === 'admin' || user?.isGodMode;
    const { tasks, loading, error } = useSelector((state) => state.scheduler);

    const [updatingId, setUpdatingId] = useState(null);
    const [triggeringId, setTriggeringId] = useState(null);
    const [historyDataSourceId, setHistoryDataSourceId] = useState(null);
    const [actionError, setActionError] = useState(null);

    useEffect(() => {
        dispatch(fetchSchedulerTasks());
    }, [dispatch, activeTenant?.tenant_id]);

    const handlePresetChange = async (dataSourceId, preset) => {
        setUpdatingId(dataSourceId);
        setActionError(null);
        try {
            await dispatch(updateSchedulerTask({ dataSourceId, preset })).unwrap();
        } catch (err) {
            setActionError(`Failed to update ${dataSourceId}: ${err}`);
        } finally {
            setUpdatingId(null);
        }
    };

    const handleTrigger = async (dataSourceId) => {
        setTriggeringId(dataSourceId);
        setActionError(null);
        try {
            await dispatch(triggerSchedulerTask({ dataSourceId })).unwrap();
            // Refresh tasks to get updated last_run.
            dispatch(fetchSchedulerTasks());
        } catch (err) {
            setActionError(`Failed to trigger ${dataSourceId}: ${err}`);
        } finally {
            setTriggeringId(null);
        }
    };

    if (loading) {
        return <div className="py-8 text-center text-gray-500">Loading pipelines…</div>;
    }

    if (error) {
        return <div className="py-8 text-center text-red-600">Error: {error}</div>;
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Pipelines</h2>
                    <p className="text-sm text-gray-500">Per-source sync schedules and run history</p>
                </div>
                <button
                    onClick={() => dispatch(fetchSchedulerTasks())}
                    className="text-sm text-gray-500 hover:text-gray-800 border border-gray-300 rounded px-3 py-1"
                >
                    Refresh
                </button>
            </div>

            {actionError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    {actionError}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                        <tr>
                            <th className="px-4 py-3 text-left">Pipeline</th>
                            <th className="px-4 py-3 text-left">Auth Status</th>
                            <th className="px-4 py-3 text-left">Schedule</th>
                            <th className="px-4 py-3 text-left">Last Run</th>
                            <th className="px-4 py-3 text-left">Next Run</th>
                            <th className="px-4 py-3 text-left">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {tasks.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                    No data sources found.
                                </td>
                            </tr>
                        )}
                        {tasks.map((item) => {
                            const currentPreset = item.task.schedule_preset ?? 'disabled';
                            const isUpdating = updatingId === item.data_source_id;
                            const isTriggering = triggeringId === item.data_source_id;

                            return (
                                <tr key={item.data_source_id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-gray-900">{item.data_source_name}</div>
                                        <div className="text-xs text-gray-500">{item.source_type}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={item.auth_status} />
                                    </td>
                                    <td className="px-4 py-3">
                                        {isAdmin ? (
                                            <PresetSelect
                                                value={currentPreset}
                                                onChange={(preset) => handlePresetChange(item.data_source_id, preset)}
                                                disabled={isUpdating}
                                            />
                                        ) : (
                                            <span className="capitalize">{currentPreset}</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {item.last_run ? (
                                            <span
                                                className="cursor-pointer hover:underline"
                                                onClick={() => setHistoryDataSourceId(item.data_source_id)}
                                                title="View history"
                                            >
                                                {formatRelativeTime(item.last_run.started_at)}{' '}
                                                <RunStatusIcon status={item.last_run.status} />
                                            </span>
                                        ) : (
                                            <span
                                                className="text-gray-400 cursor-pointer hover:underline"
                                                onClick={() => setHistoryDataSourceId(item.data_source_id)}
                                            >
                                                Never
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {formatNextRun(item.next_run_at)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {/* Trigger available for all sources; admin only (D20, D21) */}
                                            {isAdmin && (
                                                <button
                                                    onClick={() => handleTrigger(item.data_source_id)}
                                                    disabled={isTriggering}
                                                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                    title="Run now"
                                                >
                                                    {isTriggering ? '…' : '▶'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {historyDataSourceId && (
                <RunHistoryPanel
                    dataSourceId={historyDataSourceId}
                    onClose={() => setHistoryDataSourceId(null)}
                />
            )}
        </div>
    );
}
